import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FullReport, ReportSummary } from './Components';
import { SurveyResponses } from './Components/SurveyResponses';
import { Fund, Project, ProjectUpdate } from '@Types';
import { Loader, OldTabs } from '@Components';
import { Grid } from '@material-ui/core';
import { useGetLocalAuthoritiesWithFeature, useGetResponsesCount } from '@Hooks/Api';
import { useRouter } from '@Helpers/useRouter';
import router from 'next/router';
import { useGetCurrentTab } from '@Hooks/useGetCurrentTab';
import { PaginatedResponse } from '@Hooks/usePaginatedRequest';
import { Button } from '@Components/common';
import { Routes } from '@App/paths';
import { AFRightUpIcon } from '@Components/Icons/AFRightUpIcon';
import classnames from 'classnames';
import { RiBarChartFill, RiArrowLeftLine } from 'react-icons/ri';
import styles from './FundResults.module.scss';

interface FundResultsProps {
  fundId: number;
  fundData: Fund;
  fundedProjectsList: Project[];
  updatesPaginatedResponse: PaginatedResponse<ProjectUpdate>;
}

export const FundResults: React.FC<FundResultsProps> = ({
  fundId,
  fundData,
  fundedProjectsList,
  updatesPaginatedResponse,
}) => {
  const { t } = useTranslation('fund');
  const hasEnhancedReporting = Boolean(fundData.user_has_enhanced_reporting);
  const hasIndividualResponsesAccess = Boolean(fundData.user_has_individual_responses);
  const fundName = fundData.details.name;
  const status = fundData.status;

  const [
    getLocalAuthoritiesWithFeature,
    { data: localAuthoritiesWithFeature, loading: localAuthoritiesWithFeatureLoading },
  ] = useGetLocalAuthoritiesWithFeature();

  useEffect(() => {
    if (fundData.details.local_authorities && fundData.details.local_authorities.length > 0) {
      getLocalAuthoritiesWithFeature(fundData.details.local_authorities);
    }
  }, [fundData.details.local_authorities, getLocalAuthoritiesWithFeature]);

  const [getResponsesCount, { loading: isGetResponsesCountLoading, data: responsesCount }] =
    useGetResponsesCount();
  useEffect(() => {
    getResponsesCount(fundId);
  }, [getResponsesCount, fundId]);
  const { params } = useRouter();
  const tabs = useMemo(
    () => [
      {
        title: `${t('management.results.tabs.summary')}`,
        slug: 'summary',
      },
      {
        title: `${t('management.results.tabs.responses')}`,
        slug: 'responses',
        disabled: !hasIndividualResponsesAccess || !responsesCount,
      },
      {
        title: `${t('management.results.tabs.report')}`,
        slug: 'report',
        disabled: !hasEnhancedReporting || !responsesCount,
      },
    ],
    [hasEnhancedReporting, hasIndividualResponsesAccess, responsesCount, t],
  );

  let currentTab = useGetCurrentTab(params.fundResultsSlug?.[0], tabs);

  const setTab = useCallback(
    (tabNumber: number) => {
      return router.push(`/fund/${fundId}/manage/results/${tabs[tabNumber].slug}`, undefined, {
        shallow: true,
      });
    },

    [fundId, tabs],
  );

  return (
    <Grid container spacing={3} style={{ marginTop: 0 }}>
      <Grid item xs={12}>
        <div className={styles.headerWrapper}>
          <Button
            buttonType="text"
            size="small"
            startIcon={<RiArrowLeftLine size={12} />}
            className={styles.backButton}
            to={Routes.FUND_MANAGEMENT(fundId)}
          >
            {t('management.return_to_fund_management')}
          </Button>
          <div className={styles.headerContent}>
            <div>
              <h1 className={styles.fundName}>{fundName}</h1>
              <div className={classnames(styles[status], styles.statusContainer)}>
                <AFRightUpIcon className={classnames(styles[status], styles.icon)} />
                {t(`dashboard:funder.fund_status.${status}`)}
              </div>
            </div>
            <div className={styles.managementButtons}>
              <Button
                buttonType="secondary"
                onClick={() => {
                  router.push({
                    pathname: Routes.FUNDER_IMPACT_DASHBOARD_NAV,
                    query: { fund_ids: [fundId.toString()] },
                  });
                }}
                startIcon={<RiBarChartFill />}
                size="small"
              >
                {t('management.view_more_impact')}
              </Button>
            </div>
          </div>
        </div>
      </Grid>
      <Grid item xs={12}>
        <OldTabs tabs={tabs} currentTab={currentTab} onTabChange={setTab} />
      </Grid>
      {currentTab === 0 && (
        <Grid item xs={12}>
          {localAuthoritiesWithFeatureLoading ? (
            <Loader />
          ) : (
            <ReportSummary
              fundId={fundId}
              fundData={fundData}
              localAuthorities={localAuthoritiesWithFeature}
              fundedProjectsList={fundedProjectsList}
              updatesPaginatedResponse={updatesPaginatedResponse}
            />
          )}
        </Grid>
      )}
      {currentTab === 1 && (
        <Grid item xs={12}>
          <SurveyResponses fundId={fundId} />
        </Grid>
      )}
      {currentTab === 2 && (
        <Grid item xs={12}>
          {localAuthoritiesWithFeatureLoading ? (
            <Loader />
          ) : (
            <FullReport
              fundId={fundId}
              fundData={fundData}
              localAuthorities={localAuthoritiesWithFeature}
              fundedProjectsList={fundedProjectsList}
              updatesPaginatedResponse={updatesPaginatedResponse}
            />
          )}
        </Grid>
      )}
    </Grid>
  );
};
